import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider.tsx";
import { useEndpointProvider } from "../../../Providers/EndpointProvider";
import { useEnvironmentVariablesProvider } from "../../../Providers/EnvironmentVariablesProvider.tsx";
import { useAuthProvider } from "../../../Providers/AuthProvider";
import { CircularProgress, Box, TextField, InputAdornment } from "@mui/material";
import { MdSearch } from "react-icons/md";
import { MobergFontFamily } from "../../../Components/MobergFont/MobergFont";
import { localizeDate } from "../../../Computation/utilFunctions";
import { FRONTEND_LINKS } from "../../../Constants/FrontendLinks";
import RMMobileStickyHeader from "./RMMobileStickyHeader";
import StatusBadge from "../components/StatusBadge";
import { ACTIVE_MONITORING_STATUSES, GATEWAY_STATUS, URL_PARAMS, MODAL_TYPE, RM_STEPS } from "../constants";

const RMMobileSelectGateway = (props) => {
    const { setNavPage } = props;
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const { LINKS } = useBackendLinksProvider();
    const endpointProvider = useEndpointProvider();
    const { environmentVariables } = useEnvironmentVariablesProvider();
    const authProvider = useAuthProvider();
    const [gatewayData, setGatewayData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const handleLogout = () => {
        authProvider.logout();
        navigate("/");
    };

    useEffect(() => {
        setNavPage(false);
        
        // Sync URL params for cross-platform compatibility
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.has(URL_PARAMS.MODAL)) {
            urlParams.set(URL_PARAMS.MODAL, MODAL_TYPE.REMOTE_MONITORING);
            urlParams.set(URL_PARAMS.STEP, RM_STEPS.SELECT_GATEWAY);
            window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`);
        }
    }, []);

    const calculateDuration = (startTime, endTime) => {
        if (!startTime) return "-";
    
        const start = new Date(startTime);
        const end = endTime ? new Date(endTime) : new Date();
        const diff = end - start;
    
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) return `${days}d ${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    useEffect(() => {
        const getGateways = async () => {
            setIsLoading(true);
            const body = {
                "site_id": environmentVariables.SITE_ID
            }

            try {
                const res = await endpointProvider.get(LINKS.AGGREGATOR.GET_GATEWAYS, body);
                const gateways = res?.gateways || [];

                const detailedGateways = gateways.map((gw) => ({
                    id: gw.gateway_id,
                    number: gw.gateway_name || `Gateway-${gw.gateway_id}`,
                    occupied: gw.gateway?.status || "Not Available",
                    patientName: gw.patient?.patient_name || "-",
                    patientMRN: gw.patient?.patient_id || "-",
                    startTime: gw.start_time || null,
                    endTime: gw.end_time || null,
                    gatewayName: gw.gateway?.gateway_name || "Not Available",
                    gatewayStatus: gw.gateway?.status || "Not Available",
                }));

                setGatewayData(detailedGateways);

            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        }

        getGateways();
    }, [])

    const handleSelectGateway = async (gatewayId) => {
        try {
            const res = await endpointProvider.get(LINKS.AGGREGATOR.GET_GATEWAY_INFO, {
                gateway_id: parseInt(gatewayId, 10)
            });
            
            const gatewayStatus = res?.gateway?.status?.toUpperCase();
            const isActiveStatus = ACTIVE_MONITORING_STATUSES.includes(gatewayStatus);
            
            if (isActiveStatus) {
                navigate(`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN}?${URL_PARAMS.MODAL}=${MODAL_TYPE.REMOTE_MONITORING}&${URL_PARAMS.STEP}=${RM_STEPS.MONITORING}&${URL_PARAMS.GATEWAY_ID}=${gatewayId}`);
            } else {
                navigate(`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN}?${URL_PARAMS.MODAL}=${MODAL_TYPE.REMOTE_MONITORING}&${URL_PARAMS.STEP}=${RM_STEPS.ADD_PATIENT}&${URL_PARAMS.GATEWAY_ID}=${gatewayId}`);
            }
        } catch (error) {
            console.error("Error fetching gateway info:", error);
        }
    };

    const filteredGateways = gatewayData.filter(gw => {
        const searchLower = searchTerm.toLowerCase();
        return (
            gw.number.toLowerCase().includes(searchLower) ||
            gw.patientMRN.toLowerCase().includes(searchLower) ||
            gw.patientName.toLowerCase().includes(searchLower)
        );
    });

    return (
        <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#ffffff",
            overflow: "hidden"
        }}>
            <RMMobileStickyHeader 
                title="Select a MCP Device"
                showBackButton={false}
                onLogout={handleLogout}
            />

            {/* Scrollable Content */}
            <div style={{
                flex: 1,
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
                paddingTop: '60px'
            }}>
                {/* Instructions and Search */}
                <div style={{ 
                    padding: '16px'
                }}>
                    <div style={{
                        backgroundColor: '#F5F5F5',
                        borderRadius: '4px',
                        padding: '12px 14px',
                        fontSize: '13px',
                        color: '#1F2937',
                        lineHeight: '1.5',
                        fontWeight: '400',
                        marginBottom: '16px'
                    }}>
                        If a device is <span style={{ fontWeight: '600', color: '#DC2626' }}>off</span>, select it to turn on and assign to a patient. If <span style={{ fontWeight: '600', color: '#16A34A' }}>on</span>, select to view status and control the device.
                    </div>

                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by device, patient ID or name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <MdSearch size={20} color="#666" />
                                </InputAdornment>
                            ),
                        style: {
                            backgroundColor: "#F5F5F5",
                            borderRadius: "4px",
                            fontSize: "14px",
                            minHeight: "44px"
                        }
                        }}
                    />
                </div>

                {/* Gateway Cards */}
                <div style={{ 
                    padding: '0 16px 16px'
                }}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : filteredGateways.length === 0 ? (
                    <div style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        color: "#666",
                        fontSize: "14px"
                    }}>
                        No MCP Devices found
                    </div>
                ) : (
                    filteredGateways.map((gw) => {
                        const isActive = gw.gatewayStatus?.toUpperCase() === GATEWAY_STATUS.ACTIVE;
                        
                        return (
                            <div 
                                key={gw.id}
                                onClick={() => handleSelectGateway(gw.id)}
                            style={{
                                backgroundColor: "white",
                                borderRadius: "4px",
                                padding: "16px",
                                marginBottom: "12px",
                                border: "1px solid #E5E5E5",
                                cursor: "pointer",
                                transition: "all 0.2s ease"
                            }}
                            >
                                {/* Header Row */}
                                <div style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "12px"
                                }}>
                                    <div style={{
                                        fontWeight: "600",
                                        fontSize: "16px",
                                        color: "#0A84FF",
                                        fontFamily: MobergFontFamily.HEADER,
                                        textDecoration: "underline",
                                        cursor: "pointer"
                                    }}>
                                        {gw.number}
                                    </div>
                                    <StatusBadge status={gw.occupied} />
                                </div>

                                {/* Patient Info */}
                                {gw.patientMRN !== "-" && (
                                    <div style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: "12px",
                                        marginBottom: "12px",
                                        paddingTop: "12px",
                                        borderTop: "1px solid #F0F0F0"
                                    }}>
                                        <div>
                                            <div style={{
                                                fontSize: "12px",
                                                color: "#666",
                                                marginBottom: "4px"
                                            }}>
                                                Patient ID
                                            </div>
                                            <div style={{
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                color: "#2C2C2C"
                                            }}>
                                                {gw.patientMRN}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{
                                                fontSize: "12px",
                                                color: "#666",
                                                marginBottom: "4px"
                                            }}>
                                                Patient Name
                                            </div>
                                            <div style={{
                                                fontSize: "14px",
                                                fontWeight: "600",
                                                color: "#2C2C2C"
                                            }}>
                                                {gw.patientName}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {gw.startTime && (
                                    <div style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: "12px",
                                        paddingTop: "12px",
                                        borderTop: "1px solid #F0F0F0"
                                    }}>
                                        <div>
                                            <div style={{
                                                fontSize: "12px",
                                                color: "#666",
                                                marginBottom: "4px"
                                            }}>
                                                Started
                                            </div>
                                            <div style={{
                                                fontSize: "13px",
                                                color: "#2C2C2C"
                                            }}>
                                                {localizeDate(new Date(gw.startTime))}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{
                                                fontSize: "12px",
                                                color: "#666",
                                                marginBottom: "4px"
                                            }}>
                                                Duration
                                            </div>
                                            <div style={{
                                                fontSize: "13px",
                                                color: "#2C2C2C"
                                            }}>
                                                {calculateDuration(gw.startTime, gw.endTime)}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
                </div>
            </div>
        </div>
    );
};

export default RMMobileSelectGateway;

